import { NavLink } from "react-router-dom";

import { DataTableCard } from "../components/DataTableCard";
import { MetricCard } from "../components/MetricCard";
import { NumberedListCard, type NumberedListItem } from "../components/NumberedListCard";
import { PremiumHero } from "../components/PremiumHero";
import { SignalPill } from "../components/SignalPill";
import { SignalStrip } from "../components/SignalStrip";
import { useContent } from "../hooks/useContent";
import { PageContainer } from "../layout/PageContainer";
import { api, type FinancialDatumData, type FinancialSectionData, type FinancialTableGroupData } from "../lib/api";

type FinancialsPageData = Awaited<ReturnType<typeof loadFinancialsPage>>;

async function loadFinancialsPage() {
  return api.getFinancials();
}

function formatCurrency(value: number | undefined) {
  return typeof value === "number" ? `$${value.toLocaleString()}` : "Not surfaced";
}

function formatLodgingTax(percent: number | undefined, perNight: number | undefined) {
  if (percent === undefined || perNight === undefined) {
    return "Not surfaced";
  }

  return `${percent}% + ${formatCurrency(perNight)} / room night`;
}

function flattenDatums(section: FinancialSectionData) {
  return [
    ...section.verifiedFacts,
    ...section.modeledAssumptions,
    ...section.derivedOutputs,
    ...section.unresolvedItems
  ];
}

function findDatum(section: FinancialSectionData, patterns: string[]) {
  const loweredPatterns = patterns.map((pattern) => pattern.toLowerCase());

  return flattenDatums(section).find((item) => {
    const label = item.label.toLowerCase();
    return loweredPatterns.some((pattern) => label === pattern || label.includes(pattern));
  });
}

function findGroup(section: FinancialSectionData, title: string) {
  const lowered = title.toLowerCase();
  return section.tableGroups.find((group) => group.title.toLowerCase().includes(lowered));
}

function buildTableRows(group: FinancialTableGroupData | undefined) {
  if (!group) {
    return [];
  }

  return group.rows.map((row) => group.columns.map((column) => row[column] ?? ""));
}

function buildInfoClassRows(items: FinancialDatumData[], limit = items.length) {
  return items.slice(0, limit).map((item) => [item.label, item.value]);
}

function extractExcludedItems(notes: string[]) {
  return notes
    .filter((note) => note.toLowerCase().startsWith("excluded"))
    .map((note) => note.replace(/^Excluded[^:]*:\s*/i, "").trim());
}

function findScenarioValue(
  group: FinancialTableGroupData | undefined,
  scenario: string,
  columns: string[]
) {
  const row = group?.rows.find((item) => item["Scenario"]?.toLowerCase() === scenario.toLowerCase());
  if (!row) {
    return "Not surfaced";
  }

  for (const column of columns) {
    if (row[column]) {
      return row[column];
    }
  }

  return "Not surfaced";
}

function buildSensitivityHighlights(section: FinancialSectionData): NumberedListItem[] {
  const derived = section.derivedOutputs.slice(0, 3).map((item) => ({
    label: item.label,
    detail: item.value
  }));
  const conclusions = section.verifiedFacts.filter((item) => item.label === "Sensitivity conclusion").slice(0, 3).map((item) => ({
    label: item.value
  }));

  return [...derived, ...conclusions].slice(0, 5);
}

function InfoClassCard({
  eyebrow,
  title,
  items
}: {
  eyebrow: string;
  title: string;
  items: FinancialDatumData[];
}) {
  return (
    <section
      style={{
        borderRadius: "22px",
        padding: "20px",
        background: "linear-gradient(180deg, rgba(9, 14, 26, 0.88), rgba(7, 11, 21, 0.78))",
        border: "1px solid rgba(255,255,255,0.08)",
        boxShadow: "0 16px 34px rgba(3, 8, 20, 0.18)"
      }}
    >
      <div className="stack-sm">
        <div className="stack-xs">
          <span className="card-eyebrow">{eyebrow}</span>
          <h3 className="card-title">{title}</h3>
        </div>
        <div style={{ display: "grid", gap: "12px" }}>
          {items.length === 0 ? (
            <div style={{ color: "rgba(221, 231, 247, 0.76)", lineHeight: 1.5 }}>No current items surfaced in this class.</div>
          ) : (
            items.slice(0, 4).map((item, index) => (
              <div
                key={`${item.label}-${index}`}
                style={{
                  paddingTop: index === 0 ? 0 : "12px",
                  borderTop: index === 0 ? "none" : "1px solid rgba(255,255,255,0.06)"
                }}
              >
                <div style={{ color: "#f4f8ff", lineHeight: 1.45 }}>{item.label}</div>
                <div style={{ marginTop: "6px", color: "rgba(221, 231, 247, 0.76)", lineHeight: 1.5 }}>{item.value}</div>
              </div>
            ))
          )}
        </div>
      </div>
    </section>
  );
}

function FinancialSectionHeader({
  eyebrow,
  title,
  body
}: {
  eyebrow: string;
  title: string;
  body: string;
}) {
  return (
    <div className="stack-xs">
      <span className="card-eyebrow">{eyebrow}</span>
      <h2 className="card-title">{title}</h2>
      <p className="card-copy" style={{ maxWidth: "62ch" }}>{body}</p>
    </div>
  );
}

function FinancialsContent({ data }: { data: FinancialsPageData }) {
  const {
    projectFacts,
    informationClasses,
    developmentBudget,
    costBreakdown,
    operatingModel,
    underwritingScenarios,
    fallbackCase,
    sensitivities,
    assumptionsAndDisclosures
  } = data;

  const developmentBudgetGroup = findGroup(developmentBudget, "combined current selected development budget");
  const costTopLevelGroup = findGroup(costBreakdown, "top-level cost structure");
  const hardCostGroup = findGroup(costBreakdown, "hard-cost structure");
  const softCostGroup = findGroup(costBreakdown, "soft-cost structure");
  const operatingSummaryGroup = findGroup(operatingModel, "operating summary");
  const revenueAssumptionsGroup = findGroup(operatingModel, "revenue assumptions");
  const expenseGroup = findGroup(operatingModel, "operating expense structure");
  const scenarioGroup = findGroup(underwritingScenarios, "scenario table");
  const sensitivityGroup = findGroup(sensitivities, "scenario stack already locked");
  const fallbackBridgeGroup = findGroup(fallbackCase, "bridge back");
  const longTermOutputsGroup = findGroup(fallbackCase, "long-term lease outputs");
  const furnishedOutputsGroup = findGroup(fallbackCase, "furnished monthly outputs");
  const assumptionsGroups = assumptionsAndDisclosures.tableGroups.slice(0, 4);

  const budgetTotal = findDatum(developmentBudget, ["current selected development-budget total"]);
  const hardCostTotal = findDatum(developmentBudget, ["final selected hard-cost total"]);
  const softCostTotal = findDatum(developmentBudget, ["final selected soft-cost total"]);
  const baseNoi = findDatum(operatingModel, ["net operating income (noi)", "base noi"]);
  const baseYield = findDatum(underwritingScenarios, ["base yield on current selected development budget", "base yield on current selected development-budget total"]);
  const downsideYield = findDatum(underwritingScenarios, ["downside yield on current selected development budget", "downside yield on current selected development-budget total"]);
  const upsideYield = findDatum(underwritingScenarios, ["upside yield on current selected development budget", "upside yield on current selected development-budget total"]);
  const longTermFallbackYield =
    findDatum(fallbackCase, ["long-term lease fallback yield on current selected project-cost basis", "long-term lease yield on current selected project-cost basis"]) ??
    {
      label: "Long-term fallback yield",
      value: findScenarioValue(fallbackBridgeGroup, "Long-term lease fallback", [
        "Yield on Current Selected Development-Budget Total",
        "Yield on Current Selected Development Budget"
      ])
    };
  const furnishedFallbackYield =
    findDatum(fallbackCase, ["furnished monthly fallback yield on current selected project-cost basis"]) ??
    {
      label: "Furnished monthly fallback yield",
      value: findScenarioValue(fallbackBridgeGroup, "Furnished monthly fallback", [
        "Yield on Current Selected Development-Budget Total",
        "Yield on Current Selected Development Budget"
      ])
    };

  const unresolvedCount = assumptionsAndDisclosures.unresolvedItems.length;
  const excludedBudgetItems = extractExcludedItems([
    ...developmentBudget.disclosureNotes,
    ...costBreakdown.disclosureNotes
  ]);
  const knownBurdenRows = [
    ["Registration fee", formatCurrency(projectFacts.registrationFeeUsd)],
    ["Registration penalty", formatCurrency(projectFacts.registrationPenaltyUsd)],
    ["Lodging tax", formatLodgingTax(projectFacts.lodgingTaxPercent, projectFacts.lodgingTaxPerRoomNightUsd)],
    ["Utility baseline", formatCurrency(projectFacts.utilityBaselineUsd)]
  ];
  const sensitivityHighlights = buildSensitivityHighlights(sensitivities);

  const heroSubtitle = `The current selected development budget is ${budgetTotal?.value ?? "not yet surfaced"}; the base case currently reads at ${baseYield?.value ?? "not surfaced"} on cost with ${baseNoi?.value ?? "not surfaced"} of NOI, while fallback support stays weaker at ${furnishedFallbackYield.value}. This is the investor-facing summary; assumptions and unresolved items remain explicit below.`;

  return (
    <div className="stack-xl">
      <PremiumHero
        eyebrow="Financial Posture"
        title="Selected budget basis defined. Return logic visible. Downside explicit."
        subtitle={heroSubtitle}
        backgroundImage="/comps/110-f-st-map.jpg"
        backgroundLabel="Grounded local context map"
        badges={
          <>
            <SignalPill label={projectFacts.leadConcept} tone="positive" />
            <SignalPill label={`${informationClasses.length} information classes`} tone="neutral" />
            <SignalPill label={`${unresolvedCount} unresolved items`} tone="warning" />
          </>
        }
        actions={
          <>
            <NavLink to="/investment-case" className="link-button link-button-primary">
              Return to Investment Case
            </NavLink>
            <NavLink to="/market-support" className="link-button link-button-secondary">
              Open Market Support
            </NavLink>
          </>
        }
        aside={
          <div className="stack-md">
            <div className="stack-xs">
              <span className="card-eyebrow">Investor-facing summary</span>
              <div style={{ fontSize: "1.7rem", lineHeight: 1.05, letterSpacing: "-0.04em", color: "#f6fbff" }}>
                Current selected budget and scenario stack
              </div>
              <p style={{ margin: 0, color: "rgba(223,233,248,0.78)", lineHeight: 1.5 }}>
                High-level numbers lead this page. Detailed assumptions, unresolved items, and diligence boundaries sit lower in the read.
              </p>
            </div>
            <div
              style={{
                display: "grid",
                gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                gap: "12px"
              }}
            >
              {[
                ["Budget basis", budgetTotal?.value ?? "Not surfaced"],
                ["Base NOI", baseNoi?.value ?? "Not surfaced"],
                ["Base yield", baseYield?.value ?? "Not surfaced"],
                ["Fallback floor", furnishedFallbackYield.value]
              ].map(([label, value]) => (
                <div
                  key={label}
                  style={{
                    borderRadius: "18px",
                    padding: "14px",
                    background: "rgba(255,255,255,0.04)",
                    border: "1px solid rgba(255,255,255,0.08)"
                  }}
                >
                  <div style={{ fontSize: "0.72rem", letterSpacing: "0.12em", textTransform: "uppercase", color: "rgba(198,211,234,0.62)" }}>
                    {label}
                  </div>
                  <div style={{ marginTop: "8px", color: "#f4f8ff", lineHeight: 1.35 }}>{value}</div>
                </div>
              ))}
            </div>
            <div style={{ color: "rgba(216, 227, 244, 0.7)", fontSize: "0.9rem", lineHeight: 1.55 }}>
              Team &amp; Ask now carries the current capital-boundary and execution read. This page stays focused on project-cost basis, operating logic, scenario spread, downside, and disclosure posture rather than inventing a debt or equity stack.
            </div>
          </div>
        }
      />

      <SignalStrip
        items={[
          {
            label: "Budget basis",
            value: budgetTotal?.value ?? "Not surfaced",
            note: developmentBudget.summary,
            tone: "neutral"
          },
          {
            label: "Base NOI",
            value: baseNoi?.value ?? "Not surfaced",
            note: operatingModel.summary,
            tone: "positive"
          },
          {
            label: "Base yield",
            value: baseYield?.value ?? "Not surfaced",
            note: underwritingScenarios.summary,
            tone: "positive"
          },
          {
            label: "Fallback yield",
            value: furnishedFallbackYield.value,
            note: fallbackCase.summary,
            tone: "warning"
          },
          {
            label: "Open assumptions",
            value: `${unresolvedCount}`,
            note: assumptionsAndDisclosures.summary,
            tone: "warning"
          }
        ]}
      />

      <section className="stack-lg">
        <FinancialSectionHeader
          eyebrow="Development Budget / Cost Breakdown"
          title="Selected project-cost basis and cost structure"
          body="The selected development budget is now explicit, the hard-vs-soft split is visible, and excluded layers stay named instead of disappearing into prose."
        />

        <div
          style={{
            display: "grid",
            gap: "16px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))"
          }}
        >
          <MetricCard title="Selected budget basis" value={budgetTotal?.value ?? "Not surfaced"} detail="Current selected development-budget total" />
          <MetricCard title="Hard costs" value={hardCostTotal?.value ?? "Not surfaced"} detail="Final selected hard-cost total" />
          <MetricCard title="Soft costs" value={softCostTotal?.value ?? "Not surfaced"} detail="Final selected soft-cost total" />
          <MetricCard title="Excluded layers" value={`${excludedBudgetItems.length}`} detail="Still kept outside the current selected budget basis" />
        </div>

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
          }}
        >
          <DataTableCard
            eyebrow="Development Budget"
            title="Combined selected budget"
            description={developmentBudget.summary}
            columns={developmentBudgetGroup?.columns ?? ["Budget layer", "Amount"]}
            rows={buildTableRows(developmentBudgetGroup)}
            footer={
              <div style={{ color: "rgba(216, 227, 244, 0.68)", fontSize: "0.9rem", lineHeight: 1.55 }}>
                Current project-cost basis is visible here. Team &amp; Ask carries the current capital-boundary read while exact debt and equity sizing remain outside the governed public package.
              </div>
            }
          />

          <DataTableCard
            eyebrow="Cost Breakdown"
            title="Top-level cost structure"
            description={costBreakdown.summary}
            columns={costTopLevelGroup?.columns ?? ["Cost layer", "Amount", "Share"]}
            rows={buildTableRows(costTopLevelGroup)}
          />
        </div>

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
          }}
        >
          <DataTableCard
            eyebrow="Hard Costs"
            title="Hard-cost structure"
            description="Largest physical cost layers inside the selected budget basis."
            columns={hardCostGroup?.columns ?? ["Layer", "Amount"]}
            rows={buildTableRows(hardCostGroup)}
          />

          <DataTableCard
            eyebrow="Soft Costs / Fees"
            title="Soft-cost structure"
            description="Consultant, fee, and setup layers carried in the current selected basis."
            columns={softCostGroup?.columns ?? ["Layer", "Amount"]}
            rows={buildTableRows(softCostGroup)}
            footer={
              excludedBudgetItems.length > 0 ? (
                <div style={{ color: "rgba(216, 227, 244, 0.68)", fontSize: "0.9rem", lineHeight: 1.55 }}>
                  Excluded from the current public budget basis: {excludedBudgetItems.join("; ")}
                </div>
              ) : null
            }
          />
        </div>
      </section>

      <section className="stack-lg">
        <FinancialSectionHeader
          eyebrow="Operating Model"
          title="Known burdens separated from modeled operating loads"
          body="Recurring charges, compliance burdens, revenue assumptions, and the resulting operating summary are shown separately so the case does not depend on hidden operating logic."
        />

        <div
          style={{
            display: "grid",
            gap: "16px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))"
          }}
        >
          <MetricCard
            title="Utility baseline"
            value={formatCurrency(projectFacts.utilityBaselineUsd)}
            detail="Known city-side utility anchor"
          />
          <MetricCard title="Registration fee" value={formatCurrency(projectFacts.registrationFeeUsd)} detail="Known statutory burden" />
          <MetricCard title="Registration penalty" value={formatCurrency(projectFacts.registrationPenaltyUsd)} detail="Exposure if not compliant" />
          <MetricCard title="Lodging tax" value={formatLodgingTax(projectFacts.lodgingTaxPercent, projectFacts.lodgingTaxPerRoomNightUsd)} detail="Known room-night burden" />
        </div>

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
          }}
        >
          <DataTableCard
            eyebrow="Known burdens"
            title="Current fixed financial obligations"
            description="Known public charges carried into the operating read."
            columns={["Item", "Current read"]}
            rows={knownBurdenRows}
          />

          <DataTableCard
            eyebrow="Modeled loads"
            title="Revenue and operating assumptions"
            description="Modeled loads remain visible as assumptions instead of silently blending into the headline economics."
            columns={revenueAssumptionsGroup?.columns ?? ["Assumption", "Amount / value", "Status"]}
            rows={buildTableRows(revenueAssumptionsGroup)}
          />
        </div>

        <DataTableCard
          eyebrow="Operating Summary"
          title="Operating summary and expense stack"
          description={operatingModel.summary}
          columns={operatingSummaryGroup?.columns ?? ["Item", "Amount"]}
          rows={buildTableRows(operatingSummaryGroup)}
          footer={
            expenseGroup ? (
              <div style={{ color: "rgba(216, 227, 244, 0.68)", fontSize: "0.9rem", lineHeight: 1.55 }}>
                Expense structure remains modeled. Detailed line items below preserve the burden stack without overstating certainty.
              </div>
            ) : null
          }
        />
      </section>

      <section className="stack-lg">
        <FinancialSectionHeader
          eyebrow="Underwriting Scenarios"
          title="Comparative scenario view"
          body="Scenario posture is now comparative instead of narrative: downside, base, and upside outputs sit next to each other so the current best case and constrained cases can be read in one pass."
        />

        <div
          style={{
            display: "grid",
            gap: "16px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))"
          }}
        >
          <MetricCard title="Downside yield" value={downsideYield?.value ?? "Not surfaced"} detail="Compressed case on selected budget basis" />
          <MetricCard title="Base yield" value={baseYield?.value ?? "Not surfaced"} detail="Current lead underwriting case" />
          <MetricCard title="Upside yield" value={upsideYield?.value ?? "Not surfaced"} detail="Informative, not the planning default" />
          <MetricCard title="Scenario stack" value={`${scenarioGroup?.rows.length ?? 0}`} detail="Comparative rows currently surfaced" />
        </div>

        <DataTableCard
          eyebrow="Scenario Table"
          title="Current scenario comparison"
          description={underwritingScenarios.summary}
          columns={scenarioGroup?.columns ?? ["Scenario", "Value"]}
          rows={buildTableRows(scenarioGroup)}
        />
      </section>

      <section className="stack-lg">
        <FinancialSectionHeader
          eyebrow="Fallback Case"
          title="Downside economics visible and separate from the primary thesis"
          body="Fallback remains mitigation, not the main case. The page now shows the fallback floor explicitly instead of burying it in narrative support copy."
        />

        <div
          style={{
            display: "grid",
            gap: "16px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 180px), 1fr))"
          }}
        >
          <MetricCard title="Long-term fallback yield" value={longTermFallbackYield.value} detail="Unlevered downside read on current cost basis" />
          <MetricCard title="Furnished fallback yield" value={furnishedFallbackYield.value} detail="More resilient downside floor" />
          <MetricCard
            title="Long-term NOI"
            value={findDatum(fallbackCase, ["long-term lease noi"])?.value ?? "Not surfaced"}
            detail="Long-term lease fallback output"
          />
          <MetricCard
            title="Furnished NOI"
            value={findDatum(fallbackCase, ["furnished monthly noi"])?.value ?? "Not surfaced"}
            detail="Furnished monthly fallback output"
          />
        </div>

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
          }}
        >
          <DataTableCard
            eyebrow="Fallback Bridge"
            title="Bridge back to the current STR cases"
            description={fallbackCase.summary}
            columns={fallbackBridgeGroup?.columns ?? ["Case", "Yield"]}
            rows={buildTableRows(fallbackBridgeGroup)}
          />

          <DataTableCard
            eyebrow="Fallback Outputs"
            title="Fallback outputs by path"
            description="Numeric fallback outputs stay visible so downside protection is explicit."
            columns={furnishedOutputsGroup?.columns ?? longTermOutputsGroup?.columns ?? ["Output", "Value"]}
            rows={[
              ...buildTableRows(longTermOutputsGroup),
              ...buildTableRows(furnishedOutputsGroup)
            ]}
          />
        </div>
      </section>

      <section className="stack-lg">
        <FinancialSectionHeader
          eyebrow="Sensitivities"
          title="What moves the case materially"
          body="Sensitivity treatment now centers the variables and scenario movement that matter most instead of leaving risk movement inside generic commentary."
        />

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
          }}
        >
          <NumberedListCard
            eyebrow="Sensitivity Ladder"
            title="Highest-value sensitivity reads"
            description={sensitivities.summary}
            items={sensitivityHighlights}
            tone="warning"
          />

          <DataTableCard
            eyebrow="Sensitivity Base Inputs"
            title="Current base inputs and stressed stack"
            description="The page now shows both what is being stressed and how the scenario stack moves."
            columns={sensitivityGroup?.columns ?? ["Scenario", "Value"]}
            rows={buildTableRows(sensitivityGroup)}
          />
        </div>
      </section>

      <section className="stack-lg">
        <FinancialSectionHeader
          eyebrow="Assumptions & Disclosures"
          title="Evidence classes are explicit"
          body="Verified facts, modeled assumptions, derived outputs, and unresolved items are separated so investors can tell what is known, what is modeled, what is calculated, and what is still open."
        />

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 260px), 1fr))"
          }}
        >
          <InfoClassCard eyebrow="Verified facts" title="Known anchors" items={assumptionsAndDisclosures.verifiedFacts} />
          <InfoClassCard eyebrow="Modeled assumptions" title="Current modeled loads" items={assumptionsAndDisclosures.modeledAssumptions} />
          <InfoClassCard eyebrow="Derived outputs" title="Calculated outputs" items={assumptionsAndDisclosures.derivedOutputs} />
          <InfoClassCard eyebrow="Unresolved items" title="What still moves the read" items={assumptionsAndDisclosures.unresolvedItems} />
        </div>

        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 320px), 1fr))"
          }}
        >
          {assumptionsGroups.map((group) => (
            <DataTableCard
              key={group.title}
              eyebrow="Assumptions Register"
              title={group.title}
              columns={group.columns}
              rows={buildTableRows(group)}
            />
          ))}
        </div>
      </section>

      <section
        style={{
          borderRadius: "24px",
          padding: "22px",
          background: "linear-gradient(180deg, rgba(9, 14, 26, 0.92), rgba(7, 11, 21, 0.82))",
          border: "1px solid rgba(255,255,255,0.08)",
          boxShadow: "0 18px 42px rgba(3, 8, 20, 0.24)"
        }}
      >
        <div
          style={{
            display: "grid",
            gap: "18px",
            gridTemplateColumns: "repeat(auto-fit, minmax(min(100%, 280px), 1fr))",
            alignItems: "center"
          }}
        >
          <div className="stack-xs">
            <span className="card-eyebrow">Next Financial Path</span>
            <h3 className="card-title">Detailed financial routes stay available. Team &amp; Ask now carries the current capital-boundary read.</h3>
            <p className="card-copy">
              The current governed public bundle does not yet carry a separate downloadable pro forma workbook or PDF. Underwriting and fallback detail remain available now, and Team &amp; Ask now shows the current execution and financing-disclosure posture without inventing an exact raise.
            </p>
          </div>
          <div className="row-wrap gap-sm">
            <NavLink to="/team-ask" className="link-button link-button-primary">
              Open Team &amp; Ask
            </NavLink>
            <NavLink to="/underwriting" className="link-button link-button-secondary">
              Underwriting Detail
            </NavLink>
          </div>
        </div>
      </section>
    </div>
  );
}

export function EconomicsPage() {
  const { data, loading, error } = useContent("financials-page", loadFinancialsPage);

  return (
    <PageContainer
      eyebrow="Financials"
      title="Selected budget basis. Visible returns. Explicit downside."
      lede="This page now surfaces the selected project-cost basis, cost structure, operating burden, scenario spread, fallback floor, sensitivities, and disclosure posture in one investor-facing read."
    >
      {loading ? <div className="loading-panel shimmer-card">Loading financials...</div> : null}
      {error ? <div className="error-panel">{error}</div> : null}
      {data ? <FinancialsContent data={data} /> : null}
    </PageContainer>
  );
}
